/**
 * Candidate/Applicant Portal Type Definitions
 */

// Multi-entry placement model shared by server + client. Kept as a type-only
// import so this declarations module stays runtime-free.
import type { PlacementEntry } from './lib/placementUtils';
export type { PlacementEntry };
import type { ApplicantDocumentVerdict } from './lib/applicantDocumentStatus';
export type { ApplicantDocumentVerdict };

export enum ApplicationStatus {
  DRAFT = 'Draft',
  SUBMITTED = 'Submitted',
  RETURNED = 'Returned',
  CANCELLED = 'Cancelled',
  APPLICANT_CANCELLED = 'Applicant Cancelled',
  CLOSED = 'Closed',
  // Canonical archived spelling: every existing reader (constants
  // ARCHIVED_STATUSES / CLOSED_STATUSES, applicationLock's terminal
  // ladder, the Archived Applications view) recognises 'archived'
  // (case-insensitive) — a literal 'Archive' value would be invisible
  // to all of them and the application would stay "active" everywhere.
  ARCHIVED = 'Archived'
}

export enum ActiveStatus {
  YES = 'Yes',
  NO = 'No'
}

export interface ImmediateFamilyOverseas {
  name: string;
  country: string;
  relationship: string;
}

export interface PersonalDetails {
  firstName: string;
  lastName: string;
  middleName?: string;
  fullNameLegal?: string;
  // SEVIS biographical extras
  passportName?: string;   // name as it appears on passport
  preferredName?: string;  // nickname / preferred name
  suffix?: string;         // Jr., Sr., III
  dob: string;
  gender: string;
  cityOfBirth?: string;
  countryOfBirth: string;
  countryOfCitizenship: string;
  legalPermanentResidenceCountry?: string;
  email: string;
  phone: string;
  countryCode?: string;
  mobileNumber?: string;
  // SEVIS Telephone: US + foreign
  usPhone?: string;
  foreignCountryCode?: string;
  foreignPhoneNumber?: string;
  bestTimeToCall?: string;
  ssnOrTaxId: string;
  maritalStatus: string;
  address: {
    street: string;
    apartment?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  previousTravelHistory?: string;
  previousVisaHistory?: string;
  firstTimeTraveling?: boolean;
  immediateFamilyOverseas?: ImmediateFamilyOverseas[];
  fullTimeStudent?: boolean;
  j1ProgramParticipation?: J1ProgramEntry[];
  /**
   * §3.5 (PR-3): the explicit answer to "Prior Participation in a Similar
   * Program". `undefined` = not answered yet — the select starts
   * unselected. The entries list alone cannot distinguish "No" from
   * "never asked", which is how the form used to pre-answer "No".
   */
  hasPriorParticipation?: boolean;
  // Criminal & Health Background
  criminalConviction?: boolean;
  criminalConvictionExplanation?: string;
  insurancePreexistingAcknowledged?: boolean;
  insuranceAdditionalPurchased?: boolean;
  // Additional fields for PDF template placeholders
  objective?: string;
  primaryLanguage?: string;
  otherLanguages?: string;
  otherLanguageDetail?: string;
  computerSkills?: string;
  countriesResided?: string;
  currentCountryOfResidence?: string;
  personalSkills?: string;
  leisureActivities?: string;
  hospitalityExperience?: string;
}

export interface J1ProgramEntry {
  programName: string;
  startDate?: string;
  endDate?: string;
}

export interface PassportImmigration {
  passportNumber: string;
  passportCountry: string;
  issueDate: string;
  expirationDate: string;
  visaStatus?: string;
  ds2019Number?: string;
  sevisId?: string;
  intendedArrival?: string;
  usDestination?: string;
}

export interface EducationEntry {
  id: string;
  institutionName: string;
  institutionAddress: string;
  degreeEarned: string;
  fieldOfStudy: string;
  startDate: string;
  endDate: string;
  gpa?: string;
  honors?: string;
  // Expanded fields for PDF templates
  city?: string;
  country?: string;
  startMonth?: string;
  startYear?: string;
  endMonth?: string;
  endYear?: string;
  courses?: string;
}

export interface WorkEntry {
  id: string;
  employerName: string;
  employerAddress: string;
  jobTitle: string;
  jobDescription: string;
  startDate: string;
  endDate: string;
  salary?: string;
  supervisorName?: string;
  supervisorContact?: string;
  isEmployed?: boolean;
  companyPhone?: string;
  computerPrograms?: string;
  // Expanded fields for PDF templates
  mainResponsibilities?: string;
  additionalResponsibilities?: string;
  city?: string;
  country?: string;
  startMonth?: string;
  startYear?: string;
  endMonth?: string;
  endYear?: string;
}

export interface EmergencyContact {
  id: string;
  fullName: string;
  phone: string;
  anotherPhone?: string;
  email: string;
  relationship: string;
  address?: string;
  country?: string;
  englishSpeaking?: boolean;
  language?: string;
}

export interface ProgramPreferences {
  programType: string;
  preferredIndustry: string;
  preferredState: string;
  startDateAvailability: string;
  willingnessToRelocate: boolean;
  programDuration?: string;
  programTerm?: string;
  englishProficiency?: string;
  specialSkills?: string;
  additionalNotes?: string;
  usContactName?: string;
  usContactOrg?: string;
  usContactPhone?: string;
  usContactEmail?: string;
}

/** Service provider / Sponsor partner */
export interface ServiceProvider {
  providerId: string;
  name: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  companyName?: string;
  active?: boolean;
  // SEVIS sponsor fields (expanded from sponsor role)
  tier?: 'rti' | 'batch';
  sevisProgramNumber?: string;
  sevisUserId?: string;
  supportedCategories?: string[];
  defaultExportFormat?: 'packet' | 'batch';
  notes?: string;
}

export interface AssignmentDetails {
  employerName: string;
  roleTitle: string;
  city: string;
  state: string;
  country: string;
  programTerm?: string;
}

export interface VisaDetails {
  visaApplicationNumber?: string;
  visaApprovalStatus: 'Pending' | 'Approved' | 'Rejected' | string;
  visaType?: string;
  visaNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  embassyAppointmentDate?: string;
  embassyLocation?: string;
  embassyNotes?: string;
  ds160ConfirmationNumber?: string;
  ds160SubmissionDate?: string;
}

export interface FlightDetails {
  airline: string;
  flightNumber: string;
  departureDateTime: string;
  arrivalDateTime: string;
  departureAirport: string;
  arrivalAirport: string;
  additionalNotes?: string;
}

export interface ProgramPricingVariable {
  id: string;
  name: string;
  description?: string;
  sellingPrice?: number;
  costPrice?: number;
  code: string;
  visibility: 'visible' | 'hidden' | 'disabled';
  currency?: string;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ACDRecord {
  id: string;
  programId: string;
  pricingVariableId: string;
  sponsorId: string;
  sponsorName?: string;
  actualCost: number;
  invoiceTemplateId?: string;
  acdCode: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Program {
  id: string;
  programId?: string;
  name: string;
  /** Unique 5-digit system-generated program code (e.g. "1001") — searchable in the application wizard */
  code?: string;
  description: string;
  pricingVariables?: ProgramPricingVariable[];
  acdRecords?: ACDRecord[];
  requiredDocuments: string[];
  active: boolean;
  /** Eligibility requirements displayed during multi-step wizard (rich text / multi-line) */
  requirements?: string;
  /** Available terms for this program (e.g. ['Summer 2027', 'Fall 2027']) */
  terms?: string[];
  /** Wave 4: applicant-facing program terms & conditions text. */
  termsAndConditions?: string;
  /** Wave 4 (D-13): minimum applicant age; unset → default 18. */
  minimumAge?: number;
  /** Program-specific Skills & Abilities config (see lib/programSkills). */
  skillsConfig?: import('./lib/programSkills').ProgramSkillsConfig;
  /**
   * Official document requirements and validation rules for this program.
   * Acts as the source of truth for AI document review — defines required
   * document types, acceptable formats, validity rules (expiry, issuance),
   * quality standards (legibility, completeness), and any program-specific
   * compliance rules.
   */
  documentGuide?: string;
}

export interface Application {
  applicationId: string;
  userId: string;
  userEmail: string;
  status?: ApplicationStatus;            // DEPRECATED — use submissionStatus
  submissionStatus: ApplicationStatus;
  activeStatus: boolean;
  programType: string;
  programId?: string;
  programTerm?: string;
  currentStep?: number;
  masterStage?: number;
  financialStatus?: string;
  /** Whether the applicant confirmed program requirements in the wizard */
  requirementsConfirmed?: boolean;
  /** Whether a resume was uploaded during wizard */
  resumeUploaded?: boolean;
  /** Whether a resume was parsed during wizard */
  resumeParsed?: boolean;
  personalDetails?: PersonalDetails;
  passportImmigration?: PassportImmigration;
  educationalBackground?: EducationEntry[];
  workExperience?: WorkEntry[];
  emergencyContacts?: EmergencyContact[];
  programPreferences?: ProgramPreferences;
  /**
   * Skills & Abilities selected from the program's predefined list
   * (single source of truth on the application record; label is a
   * snapshot refreshed from the program config while the skill is
   * active). `skillIds` is the query projection of the same selection.
   */
  selectedSkills?: import('./lib/programSkills').SelectedSkill[];
  skillIds?: string[];
  serviceProvider?: ServiceProvider;
  assignment?: AssignmentDetails;
  visaDetails?: VisaDetails;
  flightDetails?: FlightDetails;
  embassyDetails?: {
    embassyAppointmentDate?: string;
    appointmentDate?: string; // preferred field used by email templates & batch center
    embassyLocation?: string;
    interviewResult?: string;
  };
  ds160Details?: {
    ds160Number?: string;
    ds160ConfirmationNumber?: string;
    // Wave 4 (Slice D D-05): the DS-160 security question/answer is NO
    // LONGER collected — it is a government-account credential-adjacent
    // secret a non-government intermediary must not hold. The field was
    // removed from every form and save path; previously stored values
    // remain in old records until the follow-up data purge (see the
    // Wave 4 report) — nothing in the app reads or writes them now.
    ds160SubmissionStatus?: string;
  };
  profilePhotoUrl?: string;
  adminNotes?: string;
  returnNote?: string;
  placementStatus?: 'Pending' | 'Confirmed';
  /**
   * Multi-entry placement records (primary + zero or more secondary jobs).
   * Each entry keeps its full status history; cancelled records are never
   * deleted. The legacy single `placementStatus` / `selfPlacement` fields
   * remain as derived/compatibility values.
   */
  placements?: PlacementEntry[];
  /** ISO timestamp of the most recent placement mutation. */
  placementUpdatedAt?: string;
  talentPoolEnabled?: boolean;
  tags?: string[];
  documentRequired?: string;
  createdAt: string;
  updatedAt: string;
  /** ISO timestamp of when the applicant completed identity verification (stamped at submission time) */
  applicantVerifiedAt?: string;
  // ── NEW: Reminder tracking fields ──
  embassyReminders?: Partial<Record<EmbassyReminderStage, string>>;    // stage → ISO sentAt timestamp
  completionReminders?: Partial<Record<CompletionReminderStage, string>>; // stage → ISO sentAt timestamp
  lastDocumentReminderAt?: string; // ISO timestamp of last document reminder sent

  // ── Sponsor fields ──
  sponsorId?: string;
  sponsorName?: string;
  sponsorshipStatus?: SponsorshipStatus;
  sponsorshipExportType?: 'packet' | 'batch';
  sponsorExportGeneratedAt?: string;
  sponsorExportGeneratedBy?: string;
  sevisId?: string;
  ds2019Number?: string;
  ds2019IssuedAt?: string;
  sponsorNotes?: string;
  // ── Payment tracking (set by sponsor) ──
  programFeePaid?: boolean;
  programFeePaidAt?: string;
  programFeeConfirmedBy?: string;

  // ── T/IPP fields ──
  programBeginDate?: string;   // yyyy-MM-dd, program start
  programEndDate?: string;     // yyyy-MM-dd, program end
  trainingPlan?: TrainingPlan;

  // ── SEVIS Financial Info ──
  receivedUSGovtFunds?: boolean;   // whether applicant receives US government funds
  programSponsorFunds?: number;    // financial support from program sponsor ($)
  personalFunds?: number;          // personal funds ($)
  otherFundsName?: string;         // other funding source name
  otherFundsAmount?: number;       // other funding amount ($)
  expenseTotal?: number;           // total program expense ($)

  // ── SEVIS Program fields ──
  subjectFieldCode?: string;       // CIP code e.g. "52.0201"
  occupationCategoryCode?: string; // O*NET-SOC code (Intern/Trainee only)
  matriculation?: string;          // 01=High School, 02=Associate, 03=Bachelor, 04=Master, 05=Doctorate, 06=Other
  evCreateReason?: EvCreateReason; // 01-06

  // ── SEVIS Address blocks ──
  foreignAddress?: SevisForeignAddress;
  usAddress?: SevisBatchAddress;
  residentialAddress?: SevisBatchAddress & {
    residentialType?: string;
    hostFamilyIndicator?: 'ARRV' | 'PERM' | 'TEMP';
    boardingSchoolName?: string;
  };

  // ── SEVIS Dependents ──
  dependents?: SevisDependent[];

  // ── Team Member Assignment ──
  /** UIDs of the workspace team members this applicant is assigned to. */
  assignedTeamMemberIds?: string[];
  /** Denormalized snapshot of the assigned team members for display. */
  assignedTeamMembers?: Array<{ uid: string; name?: string; email?: string; role?: string; profilePhotoUrl?: string }>;
  workspaceId?: string;
  active?: boolean;
  term?: string;
  referredBy?: string;
  referredByName?: string;
  /** Soft-archive flag — archived records are hidden client-side. */
  isArchived?: boolean;
}

export interface DocumentExtractionField {
  value: string;
  confidence: number;
  [key: string]: any;
}

/** Which engine actually read a document. Recorded honestly on every
 *  extraction so admins can see whether Google Document AI scanned the
 *  document or a built-in fallback did (and why). */
export type DocumentScanEngine =
  | 'document-ai'        // Google Cloud Document AI (OCR processor)
  | 'document-ai-id'     // Google Cloud Document AI (identity proofing processor)
  | 'tesseract-ocr'      // built-in OCR (photos + rendered PDF pages)
  | 'pdf-text'           // built-in digital-PDF text extraction
  | 'docx-text'          // built-in Word text extraction
  | 'unreadable';        // no engine could read the file

export interface DocumentExtractionData {
  status: 'pending' | 'processing' | 'complete' | 'failed' | 'needs_manual_review';
  provider: 'document-ai' | 'vision-ocr' | 'local-text';
  /** The engine that actually produced the text/fields (see DocumentScanEngine). */
  engine?: DocumentScanEngine;
  /** Human-readable note when a fallback engine ran instead of Document AI. */
  engineNote?: string;
  triggeredBy: 'upload' | 'bulk_scan' | 'manual_retry';
  processedAt: string | null;
  extractedFields: Record<string, DocumentExtractionField>;
  overallConfidence: number;
  rawExtractionRef?: string;
  errorMessage?: string;
  retryCount: number;
}

/**
 * Provenance stamped on a document whenever a review decision was produced
 * by AI and then applied (by an admin accepting an AI suggestion, applying
 * an AI review result, or a guarded bulk apply). Human decisions made
 * without AI carry NO aiReview block — that absence is what distinguishes
 * a human-read approval from an AI-applied one on the record.
 */
export interface AiReviewProvenance {
  /** Model that produced the decision (workspace-configured model id). */
  model: string;
  /** The AI's decision before the admin applied it. */
  decision: 'Approved' | 'Rejected' | 'SystemPreApproved' | 'PendingAdminReview';
  /** The AI's stated reason, verbatim. */
  reason: string;
  /** uid of the admin who applied the AI decision. */
  appliedBy: string;
  appliedByName?: string;
  /** ISO timestamp of when the decision was applied to the document. */
  at: string;
  /** Which surface produced the decision. */
  source: 'suggestion' | 'review' | 'validate' | 'pipeline' | 'bulk_review' | 'bulk_review_job';
  /** Identity cross-check outcome at decision time. */
  identityStatus?: 'match' | 'mismatch' | 'insufficient_data' | 'not_applicable';
  /** Vision inspection outcome for ID-class documents. */
  visionStatus?: 'passed' | 'flagged' | 'not_run';
  /** Set when the decision was produced (and, for auto-applied
   *  approvals, applied) by a background bulk_document_review_job job —
   *  the bulkJobs document id, so any AI-applied decision can be
   *  traced back to the exact job run that produced it. */
  jobId?: string;
}

export interface DocumentJudgmentData {
  status: 'pending' | 'complete' | 'failed';
  modelTier: 'cheap' | 'escalated';
  modelUsed?: string;
  generatedAt: string | null;
  suggestionRef?: string;
}

export interface DocumentMetadata {
  docId: string;
  applicationId: string;
  userId: string;
  name: string;
  type: string;
  uploadDate: string;
  status: 'Pending' | 'SystemPreApproved' | 'Approved' | 'Rejected' | 'PendingAdminReview' | 'PreVetted' | 'FurtherReviewRequested';
  storagePath: string;
  downloadUrl?: string;
  version?: number;
  superseded?: boolean;
  rejectionNote?: string;
  rejectionReasons?: string[];
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  /** Present only when the current decision was produced by AI and applied
   *  by an admin — human decisions never carry this block. */
  aiReview?: AiReviewProvenance;
  /** Recorded when intake converted the uploaded file into a readable
   *  format (e.g. iPhone HEIC → JPEG) before storage. */
  fileConverted?: { from: string; to: string; at: string };
  /** Applicant-facing presentation of the automated check (owner
   *  addition #2): stamped server-side from the pipeline's real state.
   *  Presentation ONLY — never read by any workflow gate. */
  applicantVerdict?: ApplicantDocumentVerdict;
  /** Set when the applicant used "Request further review" on a rejected
   *  document (status FurtherReviewRequested). */
  furtherReview?: { requestedAt: string; requestedBy: string };
  reviewLogs?: DocumentReviewLog[];
  extractedText?: string;
  aiValidation?: AiValidationResult;
  validationComplete?: boolean;
  uploadedBy?: string;
  uploaderRole?: string;
  extraction?: DocumentExtractionData;
  judgment?: DocumentJudgmentData;
}

export interface ReceiptRecord {
  id?: string;
  receiptId: string;
  receiptNumber: string;
  invoiceId: string;
  workspaceId: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  paidAt: string;
  payerName: string;
  payerEmail: string;
}

export interface AiValidationResult {
  passed: boolean;
  checkedAt: string;
  checks: AiValidationCheck[];
}

export interface AiValidationCheck {
  name: string;
  passed: boolean;
  details?: string;
}

export interface DocumentReviewLog {
  id: string;
  action: 'SystemPreApproved' | 'PendingAdminReview' | 'Approved' | 'Rejected' | 'PreVetted';
  note?: string;
  rejectionReasons?: string[];
  by: string;
  byName: string;
  at: string;
  /** True when this decision was produced by AI and applied by the admin
   *  (the document's aiReview block carries the full provenance). */
  aiAssisted?: boolean;
  aiModel?: string;
}

export interface SpecialRequest {
  requestId: string;
  applicationId: string;
  userId?: string;
  name: string;
  description: string;
  dueDate?: string;
  status: 'Pending' | 'Uploaded' | 'Completed';
  uploadedStoragePath?: string;
  uploadedAt?: string;
}

// All statuses Firma can return
export type SignRequestStatus =
  | 'not_sent'
  | 'pending'
  | 'viewed'
  | 'signed'
  | 'declined'
  | 'expired'
  | string; // catch-all for any future Firma statuses

export interface SignRequest {
  signRequestId: string;       // Firestore doc ID
  applicationId: string;
  firmaRequestId: string;      // ID returned by Firma API
  templateId: string;
  templateName: string;
  signerEmail: string;
  signerName: string;
  status: SignRequestStatus;
  createdAt: string;           // ISO timestamp
  updatedAt: string;
  signedDocumentUrl?: string;  // Populated once signed
  sentBy: string;              // admin email
  signingUrl?: string;         // Per-recipient signing link (https://app.firma.dev/signing/{signing_request_user_id})
  certificateUrl?: string;
}

/**
 * A pre-configured signing request template that maps Firma document fields
 * to applicant data paths. Admins create these once and reuse them to
 * speed up signing request preparation.
 */
export interface SigningTemplate {
  id: string;
  name: string;
  description: string;
  /** The Firma document template ID this maps to */
  firmaTemplateId: string;
  /** Denormalized Firma template name for display */
  firmaTemplateName: string;
  /**
   * Mapping from Firma field variable_name → applicant data source.
   * The key is the variable_name as returned by the Firma API.
   * The value describes where to find the data in the applicant's application object.
   */
  fieldMappings: Record<string, FieldMapping>;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface FieldMapping {
  /**
   * Dot-path into the application object to resolve the value.
   * e.g. "personalDetails.firstName" or "address.city"
   */
  fieldPath: string;
  /** Fallback value if the resolved data is empty */
  defaultValue?: string;
}

export interface Comment {
  commentId: string;
  applicationId: string;
  adminId: string;
  adminName: string;
  text: string;
  createdAt: string;
  updatedAt?: string;
  edited?: boolean;
  modifiedBy?: string;
  fileUrl?: string;
  fileName?: string;
  replies?: {
    replyId: string;
    adminId: string;
    adminName: string;
    text: string;
    createdAt: string;
    updatedAt?: string;
    edited?: boolean;
    modifiedBy?: string;
  }[];
}

export interface AuditLog {
  logId: string;
  adminId: string;
  adminName: string;
  action: string;
  targetId: string;
  details: string;
  timestamp: string;
  bulkGroupId?: string;
}

export type FormBuilderFieldType =
  | 'short_text'
  | 'long_text'
  | 'number'
  | 'email'
  | 'phone'
  | 'date'
  | 'dropdown'
  | 'multi_select'
  | 'radio'
  | 'checkbox'
  | 'file_upload'
  | 'yes_no';

export interface FormBuilderFieldOption {
  id: string;
  label: string;
  value: string;
}

export interface FormBuilderField {
  id: string;
  label: string;
  type: FormBuilderFieldType;
  required?: boolean;
  placeholder?: string;
  helpText?: string;
  options?: string[] | FormBuilderFieldOption[];
  defaultValue?: any;
}

export interface OrgDocumentHistoryEntry {
  action: 'uploaded' | 'replaced' | 'removed';
  by: string;
  byName?: string;
  at: string;
  note?: string;
}

export type OrgDocumentStatus = 'Valid' | 'Expired' | 'Expiring Soon' | 'No Expiry Date';

/**
 * A supporting document uploaded by an admin onto a Referrer, Sponsor,
 * or Employer record (documents subcollection on the org doc). The
 * lifecycle `status` is computed server-side from `expiryDate` at read
 * time — it is never stored and never edited by hand. `removedAt`
 * marks a tombstone: the record (and its history) survives removal.
 */
export interface OrgDocument {
  docId: string;
  orgType: 'referrer' | 'employer' | 'sponsor';
  orgId: string;
  name: string;
  type: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  expiryDate: string | null;
  uploadedBy: string;
  uploadedByName?: string;
  uploadedAt: string;
  updatedAt: string;
  removedAt?: string | null;
  history: OrgDocumentHistoryEntry[];
  status?: OrgDocumentStatus;
  expiringSoon?: boolean;
}

/**
 * A Special Request issued to an organization (Referrer / Sponsor /
 * Employer) — stored in the same `documentRequests` collection as
 * applicant requests, discriminated by subjectKind: 'organization'.
 * The recipient is the org's designated Super Agent (org doc `userId`),
 * snapshotted at send time. Verification requests (Slice 3) complete
 * only on a Didit-confirmed approval and can never be overridden.
 */
export interface OrgSpecialRequest {
  requestId: string;
  subjectKind: 'organization';
  orgType: 'referrer' | 'employer' | 'sponsor';
  orgId: string;
  orgName: string;
  recipientUserId: string;
  recipientEmail: string;
  documentName: string;
  documentType: 'Special Request';
  requestType: 'Standard' | 'Verification' | 'Form';
  responseType: string;
  description: string;
  instructions?: string;
  priority: string;
  dueDate?: string | null;
  status: string;
  token: string;
  mandatory?: boolean;
  blocksWorkspaceAccess?: boolean;
  requiresApproval?: boolean;
  templateId?: string | null;
  templateName?: string | null;
  submissions?: {
    version: number;
    submittedAt: string;
    submittedBy: string;
    responseValue?: string;
    formResponses?: Record<string, any>;
    responseFiles?: { name: string; url: string; docName?: string | null }[];
    /** Multiple Choice answers: every selected value (lossless). */
    selectedValues?: string[];
  }[];
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  reviewedByName?: string | null;
  reviewDecision?: 'Approved' | 'Rejected' | null;
  rejectionReason?: string | null;
  approvedAt?: string | null;
  approvedBy?: string | null;
  notifyAdminOnSubmit?: boolean;
  notifyAdminOnCompletion?: boolean;
  requiredDocs?: any[];
  formFields?: any[];
  dropdownOptions?: any[];
  responseValue?: string;
  formResponses?: Record<string, any>;
  responseFiles?: { name: string; url: string; docName?: string | null }[];
  /** Latest Multiple Choice selections (mirror of the newest version). */
  selectedValues?: string[];
  createdBy: string;
  createdAt: string;
  sentAt?: string | null;
  submittedAt?: string | null;
  uploadedAt?: string | null;
  completedAt?: string | null;
  completionVia?: 'review' | 'override' | 'didit' | null;
  overrideReason?: string | null;
  cancelReason?: string | null;
  diditSessionId?: string | null;
  diditWorkflowId?: string | null;
  verificationStatus?: string | null;
  verificationIssue?: string | null;
  outstanding?: boolean;
}

export interface OrgSpecialRequestTemplate {
  templateId: string;
  templateName: string;
  title: string;
  description: string;
  instructions: string;
  targetOrgTypes: ('referrer' | 'employer' | 'sponsor')[];
  /** 'Form' = the organization completes a form (formFields).
   * Absent on templates written before the Form type existed. */
  requestType?: 'Standard' | 'Form';
  responseType: string;
  priority: string;
  requiredDocs: any[];
  formFields: any[];
  dropdownOptions: any[];
  textResponseType?: string | null;
  defaultDueDays: number | null;
  blocksWorkspaceAccess: boolean;
  requiresApproval: boolean;
  notifyAdminOnSubmit: boolean;
  notifyAdminOnCompletion: boolean;
  isActive: boolean;
  workspaceId?: string;
  createdBy?: string;
  createdByName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface DocumentRequest {
  requestId: string;
  applicantId: string;
  userId: string;
  documentName: string;
  documentType: 'Required' | 'Optional' | 'Situational' | string;
  description: string;
  dueDate?: string;
  priority: 'Low' | 'Medium' | 'High' | string;
  status: 'Pending' | 'Uploaded' | 'Completed' | string;
  createdBy: string;
  createdAt: string;
  uploadedAt?: string;
  uploadedStoragePath?: string;
  uploadedDownloadUrl?: string;
  formFields?: FormBuilderField[];
}

export interface DiditAuditEntry {
  action: 'reverification_requested' | 'reverification_completed' | 'verification_completed' | 'reminder_sent' | 'verification_link_sent';
  performedBy: string;
  performedByName?: string;
  reason?: string;
  timestamp: string;
}

/** Country metadata for international phone input */
export interface CountryPhoneMeta {
  code: string;       // ISO alpha-2
  dial: string;       // e.g. '+1'
  name: string;
  flag: string;       // emoji flag
  placeholder: string; // example number
  pattern: RegExp;    // validation regex for local digits
}

export type PrimaryRole =
  | 'applicant'
  | 'employer'
  | 'sponsor'
  | 'referrer'
  | 'admin'
  | 'platform_admin';

export interface Address {
  street: string;
  apartment?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface Organization {
  id: string;
  workspaceId: string;

  type: 'employer' | 'sponsor' | 'referrer';
  entityKind: 'organization' | 'individual';

  name: string;
  legalName?: string;
  slug?: string;
  logoUrl?: string;
  emailLogoUrl?: string;
  loginLogoUrl?: string;

  contactEmail?: string;
  contactPhone?: string;
  address?: Address;

  status: 'active' | 'inactive' | 'archived';
  primaryAccountUid?: string;

  settings?: Record<string, unknown>;
  metadata?: Record<string, unknown>;

  createdAt: string;
  updatedAt: string;
  createdBy: string;
  archivedAt?: string;
}

export interface OrganizationMembership {
  uid: string;
  organizationId: string;
  workspaceId: string;

  membershipStatus: 'invited' | 'active' | 'suspended' | 'removed';
  isPrimaryAccount: boolean;

  title?: string;
  permissions?: Record<string, 'none' | 'view' | 'edit'>;

  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface EffectiveUserIdentity {
  uid: string;
  workspaceId: string;
  primaryRole: PrimaryRole;
  workspaceRole: string;
  organizationId?: string;
  organizationType?: 'employer' | 'sponsor' | 'referrer';
  isPrimaryAccount?: boolean;
}

export interface UserProfile {
  uid: string;
  email: string;
  role: 'applicant' | 'admin' | 'employer' | 'referrer' | 'sponsor' | 'platform_admin';
  primaryRole?: PrimaryRole;
  workspaceId?: string;
  workspaceMembers?: Record<string, string>;
  /** Workspace-scoped role for team members (admin / manager / member / custom role id) */
  workspaceRole?: string;
  /** Custom role permission matrix keyed by module/tab id (e.g. { events: 'view', reports: 'none' }) */
  permissions?: Record<string, string>;
  /** Applicant visibility scope for the active workspace: 'all' | 'assigned' */
  applicantScope?: 'all' | 'assigned';
  /** Derived client flags for the global platform-super-admin tier. */
  platformAdmin?: boolean;
  isPlatformAdmin?: boolean;
  /** Enhanced referrer flag (grants access to training/marketing/team/commissions modules) */
  isEnhanced?: boolean;
  /** Referrer tier: 'enhanced' | 'standard' (or legacy string values) */
  referrerType?: string;
  organizationId?: string;
  organizationMemberships?: Record<string, OrganizationMembership>;
  hasPassword?: boolean;
  recoveryEmail?: string | null;
  firstName?: string;
  lastName?: string;
  sponsorId?: string;
  sponsorName?: string;
  sponsorTier?: 'rti' | 'batch';
  sponsorPermissionLevel?: 'standard' | 'manager';
  /** Sponsor approval gate: 'active'/'approved' = approved, 'pending' = awaiting review */
  sponsorStatus?: string;
  /**
   * Set by POST /api/auth/create-account for employer/referrer/sponsor roles:
   * the user doc carries the role but no org doc exists yet. finalizeOrgOnboard
   * deletes it once the org record is created. App routes orgSetupRequired
   * users to their org onboarding component.
   */
  orgSetupRequired?: boolean;
  /**
   * Profile completion status carried on the user doc ('incomplete' while
   * onboarding is unfinished). Read by App's org routing gates; the field
   * was always present at runtime but undeclared, so every read was a
   * TS2339 in the baseline.
   */
  status?: string;
  displayName?: string;
  phoneNumber?: string;
  /** Country dial code prefix (e.g. '+1') — stored separately for international phone */
  phoneCountryCode?: string;
  profilePhotoUrl?: string;
  /** Legacy flat address field — still read/written for backward compatibility */
  address?: string;
  /** Structured address fields */
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  howDidYouHear?: string;
  profileCompleted?: boolean;
  createdAt: string;
  blocked?: boolean;
  blockedAt?: string;
  userIdMigrated?: boolean;
  /** Didit identity verification fields — server-written only */
  didit?: {
    sessionId?: string;
    sessionUrl?: string;
    /** All possible Didit statuses mapped from API/webhook responses */
    status: 'not_started' | 'pending' | 'in_progress' | 'in_review' | 'approved' | 'declined' | 'expired' | 'abandoned' | 'kyc_expired' | 'reverification_required';
    timestamp?: string;
    verifiedAt?: string;
    lastReminderSentAt?: string;
    reverificationReason?: string;
    reverificationRequestedBy?: string;
    reverificationRequestedByName?: string;
    reverificationRequestedAt?: string;
    reverificationCompletedAt?: string;
    /** Server-internal flag set when create-session is called for a re-verification */
    isReverification?: boolean;
    /** Webhook delivery ids already applied (idempotency, capped server-side) */
    processedWebhookEventIds?: string[];
    audit?: DiditAuditEntry[];
  };
  /**
   * §3.8 (PR-3): the Didit status the identity banner was dismissed FOR.
   * Server-persisted per account: the banner stays dismissed across
   * logins/devices until the verification status CHANGES, at which point
   * the stored value no longer matches and the banner returns.
   */
  diditBannerDismissedStatus?: string;
}

export type EventReminderStage =
  | 'reminder_7d'
  | 'reminder_3d'
  | 'reminder_1d'
  | 'reminder_2h'
  | 'reminder_30m';

export interface GroupEventSlot {
  id: string;
  startTime: string; // "HH:MM"
  endTime: string;   // "HH:MM"
  bookedBy?: {
    name: string;
    email: string;
    applicationId?: string;
  };
  bookingStatus?: 'unbooked' | 'booked';
  meetingUrl?: string;
  checkInStatus?: 'pending' | 'checked_in' | 'no_show';
  result?: 'Pass' | 'Rejected' | 'Follow-up Required' | string;
}

export interface BlockedPeriod {
  id: string;
  startTime: string; // "HH:MM"
  endTime: string;   // "HH:MM"
  label: string;
}

export interface GroupEventConfig {
  sessionDuration: number;
  bufferBefore: number;
  bufferAfter: number;
  blockedTimes: BlockedPeriod[];
  bookingOpensType: 'immediately' | 'scheduled';
  bookingOpensAt?: string;
  bookingClosesType: 'hoursBefore' | 'scheduled';
  bookingClosesHours?: number;
  bookingClosesAt?: string;
  isVirtual: boolean;
  location: string;
}

export interface Event {
  eventId: string;
  title: string;
  eventType: 'Embassy' | 'Interview' | 'Orientation' | 'Training' | 'Flight' | 'Medical' | 'Other' | string;
  dateTime: string;
  location: string;
  description?: string;
  status: 'Scheduled' | 'Completed' | 'Cancelled' | string;
  applicantId: string;
  applicantUserId?: string;
  timezone?: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt?: string;
  reminders?: Partial<Record<EventReminderStage, string>>; // stage → ISO sentAt timestamp
  // Virtual meeting fields
  isVirtual?: boolean;
  meetingUrl?: string;
  meetingProvider?: 'jitsi' | 'daily' | 'google_meet' | 'zoom' | 'other';
  videoProvider?: 'jitsi' | 'daily';
  dailyRoomName?: string;
  dailyRoomUrl?: string;
  dailyRoomStatus?: 'active' | 'cleanup_pending' | 'recording_processing' | 'retained' | 'deleted' | 'cleanup_failed';
  archived?: boolean;
  assignedTeam?: string[];
  dailySettingsOverride?: Partial<DailyWorkspaceSettings>;
  /** Meeting destination type: 'physical' | 'url' | 'phone' | 'jitsi' | 'daily'. */
  locationType?: 'physical' | 'url' | 'phone' | 'jitsi' | 'daily' | string;
  /** Meeting length in minutes. */
  durationMinutes?: number;
  /** Additional participant emails/uids. */
  participants?: string[];
  /** Minutes before the event to send a reminder. */
  reminderMinutes?: number;
  // Assignment fields
  assignedTo?: string;       // UID of the agent/admin/referrer assigned
  assignedToName?: string;   // Display name of assignee
  assignedToRole?: 'admin' | 'employer' | 'referrer' | 'sponsor';
  // Scheduling / invitation fields
  isSchedulingRequest?: boolean;        // true = pending applicant time selection
  schedulingToken?: string;             // Unique token for public scheduling page
  schedulingConfirmed?: boolean;        // true after applicant picks time
  schedulingExpiresAt?: string;         // ISO timestamp when scheduling link expires
  // Group Event Fields
  isGroupEvent?: boolean;
  groupEventConfig?: GroupEventConfig;
  slots?: GroupEventSlot[];
  /** Per-application invitation record: { [applicationId]: { registrationDate?, status? } } */
  invitations?: Record<string, { registrationDate?: string; status?: string }>;
  /** Backwards-compatible list of invited application ids */
  invitationList?: string[];
  // Notes and Results
  internalNotes?: string;
  externalNotes?: string;
  result?: 'Pass' | 'Rejected' | 'Follow-up Required' | string;
}

// ── Agent Availability ──────────────────────────────────────────────
export interface TimeSlot {
  dayOfWeek: number;  // 0=Sun, 1=Mon, ..., 6=Sat
  startTime: string;  // "HH:mm" in 24h format
  endTime: string;    // "HH:mm" in 24h format
}

export interface DateOverride {
  date: string;       // "YYYY-MM-DD"
  available: boolean; // false = blocked, true = extra availability
  startTime?: string; // "HH:mm" (only if available=true and differs from regular)
  endTime?: string;
  reason?: string;
}

export interface AgentAvailability {
  availabilityId: string;
  userId: string;
  userEmail: string;
  userName: string;
  timezone: string;
  weeklySlots: TimeSlot[];
  dateOverrides: DateOverride[];
  bufferMinutes: number;      // Minutes between meetings (default 15)
  minNoticeHours: number;     // Minimum hours advance booking (default 24)
  maxDaysAdvance: number;     // Max days in advance (default 30)
  isActive: boolean;
  updatedAt: string;
}

export interface SchedulingRequest {
  requestId: string;
  eventId?: string;
  token: string;
  applicantId: string;
  applicantEmail: string;
  applicantName: string;
  agentId: string;
  agentName: string;
  title: string;
  description?: string;
  eventType: string;
  durationMinutes: number;
  timezone: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'expired';
  selectedDate?: string;       // "YYYY-MM-DD" once confirmed
  selectedTime?: string;       // "HH:mm" once confirmed
  confirmedAt?: string;
  expiresAt: string;
  createdAt: string;
}

// ── NEW: Embassy Appointment Reminder Stages ─────────────────────────
export type EmbassyReminderStage =
  | 'embassy_4d'
  | 'embassy_2d'
  | 'embassy_24h'
  | 'embassy_5h'
  | 'embassy_1h';

// ── NEW: Application Completion Deadline Reminder Stages ────────────
export type CompletionReminderStage =
  | 'completion_20d'
  | 'completion_15d'
  | 'completion_10d'
  | 'completion_5d'
  | 'completion_24h';

export interface BatchFolder {
  folderId: string;
  name: string;
  description?: string;
  tags?: string[];
  parentId?: string | null;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
}

export interface Batch {
  batchId: string;
  name: string;
  description: string;
  programType: string;
  programTypes?: string[];
  applicantIds: string[];
  createdAt: string;
  status: 'Draft' | 'Processing' | 'Completed';
  notes?: string;
  tags?: string[];
  folderId?: string | null;
}

export interface BatchLog {
  logId: string;
  batchId: string;
  adminId: string;
  adminName: string;
  text: string;
  createdAt: string;
  fileUrl?: string;
  fileName?: string;
}

/* ── Document Templates (PDF generation) ── */

export type TemplateStatus = 'draft' | 'published' | 'archived';
export type PaperSize = 'A4' | 'Letter' | 'Legal';
export type Orientation = 'portrait' | 'landscape';

export interface DocumentTemplateVersion {
  version: number;
  htmlContent: string;
  css: string;
  notes?: string;
  createdBy: string;
  createdAt: string;
  storagePath?: string;
}

export interface DocumentTemplate {
  id: string;
  templateId: string;
  code?: string;
  name: string;
  description: string;
  category: string;
  status: TemplateStatus;
  version: number;
  paperSize: PaperSize;
  orientation: Orientation;
  margins: { top: number; right: number; bottom: number; left: number };
  htmlContent: string;
  css: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
  storagePath?: string;
}

export interface GeneratedDocument {
  id: string;
  documentId: string;
  templateId: string;
  templateName: string;
  templateVersion: number;
  applicantId: string;
  applicationId: string;
  generatedBy: string;
  generatedByName: string;
  generatedAt: string;
  pdfUrl: string;
  storagePath: string;
  fileSize: number;
  checksum: string;
  status: 'completed' | 'failed';
  errorMessage?: string;
}

/* ── Support Ticket Types ── */

export type TicketStatus = 'open' | 'in_progress' | 'pending' | 'resolved' | 'closed';
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface CommentAttachment {
  /** Stable attachment id; legacy comments may derive this from storagePath. */
  fileId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  /** Authenticated proxy URL, never a raw Storage path. */
  url: string;
  storagePath?: string;
  scanStatus?: string;
  status?: 'available' | 'processing' | 'blocked' | 'missing';
  thumbnailUrl?: string;
}

export type TicketReaction = '👍' | '❤️' | '😊' | '🎉' | '😢' | '🙏';

export interface TicketComment {
  commentId: string;
  ticketId: string;
  authorId: string;
  authorName: string;
  authorRole: 'admin' | 'applicant';
  body: string;
  isInternal: boolean; // admin-only internal notes
  attachments?: CommentAttachment[];
  reactions?: Partial<Record<TicketReaction, string[]>>; // emoji -> array of userIds
  createdAt: string;
  updatedAt?: string;
  editedAt?: string;
  /** If set, this comment is a reply/quote of another comment */
  parentId?: string;
  /** Cached snippet of the quoted message for display */
  quotedMessage?: {
    body: string;
    authorName: string;
  };
  /** True when this comment was created from an inbound email reply whose From address does not match the applicant on file */
  unverifiedSender?: boolean;
}

/**
 * A team member working a ticket. A ticket has 0..N assignees — this is a
 * many-to-many relationship, NOT a single owner.
 */
export interface TicketAssignee {
  uid: string;
  name: string;
  /** 'owner' marks the lead assignee; everyone else is a collaborator. */
  role?: 'owner' | 'collaborator';
  assignedAt?: string;
  assignedBy?: string;
}

export interface Ticket {
  ticketId: string;
  applicationId: string;
  applicantId: string;
  applicantName: string;
  applicantEmail: string;
  programId: string;
  programName: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  /** Support category id (see TICKET_CATEGORIES). Older tickets may omit it → treated as 'general'. */
  category?: string;
  /**
   * MULTI-ASSIGNEE — the source of truth for who works this ticket.
   *
   * `assignedTo` / `assignedToName` below remain the DERIVED primary owner
   * (always `assignees[0]`) so every legacy reader — ticket rows, the "me" and
   * "unassigned" filters, assignment emails — keeps working untouched. Same
   * back-compat pattern as `placements` vs `placementStatus`.
   */
  assignees?: TicketAssignee[];
  assignedTo: string | null;    // admin UID (derived owner)
  assignedToName: string | null; // admin display name (derived owner)
  createdBy: string;            // who created the ticket (UID)
  createdByRole: 'admin' | 'applicant';
  /** Org-ticket provenance: server-stamped on tickets created from an org support surface. */
  source?: string;
  employerId?: string;
  referrerId?: string;
  sponsorId?: string;
  attachments?: { name: string; url: string }[];
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  closedAt?: string;
  /** Preview of the last comment body (admin-facing: includes internal notes) */
  lastCommentBody?: string;
  /** 'admin' | 'applicant' indicating who wrote the last comment */
  lastCommentBy?: string;
  /** Timestamp of the last comment */
  lastCommentAt?: string;
  /** Display name of the last comment author */
  lastCommentAuthor?: string;
  /** Whether the last comment is an internal note (staff-only) */
  lastCommentIsInternal?: boolean;
  /** Preview of the last NON-internal comment — the only preview an
   *  applicant-facing surface may render or search */
  lastPublicCommentBody?: string;
  /** 'admin' | 'applicant' indicating who wrote the last public comment */
  lastPublicCommentBy?: string;
  /** Timestamp of the last public comment */
  lastPublicCommentAt?: string;
  /** Display name of the last public comment author */
  lastPublicCommentAuthor?: string;
  /** Map of adminUid → readTimestamp — tracks who has read this ticket */
  readBy?: Record<string, string>;
  /** Computed: whether the current admin user has unread messages */
  unread?: boolean;
  /** Computed: count of unread messages for the current admin */
  unreadCount?: number;
  /** Whether the ticket is archived */
  archived?: boolean;
  /** Chat Phase 4: the CALLER's own per-viewer pin state, projected by
      GET /api/tickets from the ticket's viewerState map. Personal list
      state — distinct from the shared lifecycle `archived` flag. */
  viewerPinned?: boolean;
  /** Chat Phase 4: the CALLER's own per-viewer archive state. */
  viewerArchived?: boolean;
}

/* ── Job Board Types ── */

export type JobStatus =
  | 'draft'
  | 'pending_review'
  | 'published'
  | 'active'
  | 'in_review'
  | 'filled'
  | 'closed'
  | 'archived';

export type JobApplicationStatus =
  | 'interested'
  | 'applied'
  | 'under_review'
  | 'shortlisted'
  | 'interviewing'
  | 'approved'
  | 'rejected'
  | 'assigned';

export type JobMatchSource = 'applicant' | 'admin' | 'employer';

export type JobMatchStatus =
  | 'interested'
  | 'applied'
  | 'pending_employer'
  | 'pending_applicant_information'
  | 'under_review'
  | 'shortlisted'
  | 'interviewing'
  | 'interview_invitation_sent'
  | 'interview_scheduled'
  | 'interview_completed'
  | 'shared_with_employer'
  | 'offer_sent'
  | 'hired'
  | 'rejected'
  | 'offer_declined'
  | 'assigned'
  | 'applicant_withdrawn'
  | 'approved'
  | 'closed';

export interface JobMatch {
  id: string;
  jobId: string;
  applicantId: string;
  applicationId?: string;     // Link to the Application record if applicant applied via job board
  source: JobMatchSource;
  status: JobMatchStatus;
  jobTitle: string;
  employerName: string;
  employerId?: string;
  notes?: string;
  createdBy: string;          // Admin UID if admin-assigned
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
  /** Runtime discriminator injected by API responses */
  _type?: 'match' | 'application';
  matchType?: 'match' | 'application';
  /** Whether the profile has been shared with the employer */
  sharedWithEmployer?: boolean;
  sharedWithEmployerAt?: string;
  /** Denormalized job snapshot for display even if job is deleted */
  jobSnapshot?: {
    title: string;
    employerName: string;
    location: string;
    jobType: string;
    status: string;
  };
}

export type JobType =
  | 'full-time'
  | 'part-time'
  | 'seasonal'
  | 'internship'
  | 'temporary'
  | 'contract';

export type EmployerStatus = 'active' | 'inactive' | 'suspended';

export interface Employer {
  id: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  website?: string;
  description?: string;
  address?: string;
  country?: string;
  industry?: string;
  logoUrl?: string;
  status: EmployerStatus;
  invitedBy?: string;       // admin UID who invited this employer
  userId?: string;          // Firebase Auth UID (legacy — primary user)
  linkedUsers?: {            // multi-user support
    uid: string;
    email: string;
    role: 'primary' | 'recruiter' | 'manager';
    status: 'active' | 'blocked' | 'removed';
    invitedAt: string;
  }[];
  createdAt: string;
  updatedAt: string;
  archived?: boolean;
  archivedAt?: string | null;
}

export interface Job {
  id: string;
  title: string;
  employerId?: string;
  employerName: string;
  employer?: {
    id: string;
    companyName: string;
    contactEmail?: string;
    contactPhone?: string;
    logoUrl?: string;
  };
  location: string;
  jobType: JobType;
  description: string;
  requirements?: string;
  salary?: string;
  compensation?: string;
  startDate?: string;
  endDate?: string;
  applicationDeadline?: string;
  status: JobStatus;
  country?: string;
  programType?: string;          // legacy single program (kept for backward compat)
  programs?: string[];           // multi-program support
  industry?: string;
  positions?: number;            // number of openings
  createdBy: string;             // admin UID
  createdByName?: string;
  submittedBy?: string;          // employer ID if employer-submitted
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  imageUrl?: string;              // URL for company / job image
  tags?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface JobApplication {
  id: string;
  jobId: string;
  jobTitle: string;
  job?: {
    title: string;
    employerName: string;
    location: string;
    jobType: string;
  };
  applicantId: string;
  applicantEmail: string;
  applicantName: string;
  applicantPhone?: string;
  applicationId?: string;         // reference to the applicant's program Application doc
  programType?: string;           // copied from the selected application at apply time
  resumeUrl?: string;
  coverLetter?: string;
  answers?: Record<string, string>; // employer questions
  status: JobApplicationStatus;
  employerDecision?:'interested' | 'not_interested' | 'hired' | 'rejected_by_employer';
  employerReviewedAt?: string;
  employerFeedback?: string;
  adminNotes?: string;
  interviewDate?: string;
  assignedAt?: string;
  appliedAt: string;
  updatedAt: string;
}

/* ── Interview Scheduling Types ── */

export type InterviewStatus =
  | 'draft'
  | 'invitation_sent'
  | 'awaiting_applicant_response'
  | 'accepted'
  | 'declined'
  | 'rescheduled'
  | 'completed'
  | 'cancelled'
  | 'no_show';

export type InterviewType = 'virtual' | 'phone' | 'in-person';

export type SchedulingMethod = 'employer_picks' | 'applicant_picks';

export interface InterviewSlot {
  slotId: string;
  date: string;       // ISO YYYY-MM-DD
  time: string;       // HH:mm
  timeZone: string;
  durationMinutes: number;
  isBooked: boolean;
  expiresAt?: string; // ISO, slot expiry for applicant picks
}

export interface Interview {
  id: string;
  applicationId: string;           // JobApplication id
  applicantId: string;
  applicantName: string;
  applicantEmail: string;
  jobId: string;
  jobTitle: string;
  employerId: string;
  employerName: string;
  employerUserId: string;
  schedulingMethod: SchedulingMethod;
  interviewType: InterviewType;
  status: InterviewStatus;
  // Option 1 — employer sets date/time
  scheduledDate?: string;          // ISO date YYYY-MM-DD
  scheduledTime?: string;          // HH:mm
  scheduledEndDate?: string;       // ISO (computed)
  timeZone?: string;
  meetingLocation?: string;
  meetingLink?: string;
  notes?: string;
  // Option 2 — applicant picks
  durationMinutes?: number;
  allowApplicantChooseDateTime: boolean;
  availableSlots?: InterviewSlot[];
  selectedSlotId?: string;
  // Status timestamps
  invitationSentAt?: string;
  invitationViewedAt?: string;
  acceptedAt?: string;
  declinedAt?: string;
  rescheduledAt?: string;
  rescheduledBy?: 'employer' | 'applicant';
  completedAt?: string;
  cancelledAt?: string;
  cancelledBy?: 'employer' | 'applicant' | 'system';
  createdAt: string;
  updatedAt: string;
}

export interface InterviewTimelineEvent {
  id: string;
  interviewId: string;
  eventType:
    | 'invitation_created'
    | 'invitation_sent'
    | 'invitation_viewed'
    | 'applicant_accepted'
    | 'applicant_declined'
    | 'interview_rescheduled'
    | 'reminder_sent'
    | 'interview_completed'
    | 'interview_cancelled'
    | 'employer_note_added';
  performedByRole: 'employer' | 'applicant' | 'system' | 'admin';
  performedByUserId?: string;
  performedByName?: string;
  timestamp: string;
  timeZone?: string;
  details?: string;
}

export interface EmployerAvailability {
  id: string;
  employerId: string;
  workingDays: number[];                          // 0–6, Sun=0
  workingHoursStart: string;                      // HH:mm
  workingHoursEnd: string;                        // HH:mm
  timeZone: string;
  bufferMinutes: number;
  maxInterviewsPerDay: number;
  minimumSchedulingNoticeHours: number;
  blackoutDates: string[];                        // ISO YYYY-MM-DD
  holidays: string[];
  temporaryUnavailability: {
    start: string;
    end: string;
    reason?: string;
  }[];
  updatedAt: string;
}

export interface InterviewNotification {
  id: string;
  recipientId: string;
  recipientRole: 'employer' | 'applicant';
  interviewId: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  sentViaEmail: boolean;
  createdAt: string;
}

export interface InterviewCalendarEvent {
  id: string;
  interviewId: string;
  applicantName: string;
  jobTitle: string;
  interviewType: InterviewType;
  status: InterviewStatus;
  scheduledDate: string;
  scheduledTime: string;
  timeZone: string;
  durationMinutes: number;
  company?: string;
}

export interface JobStatusHistory {
  id: string;
  jobId: string;
  fromStatus: JobStatus | null;
  toStatus: JobStatus;
  changedBy: string;
  changedByName: string;
  note?: string;
  changedAt: string;
}

/* ── Sponsor Dashboard Types ── */

export type SponsorshipStatus =
  | 'unassigned'
  | 'assigned'
  | 'ready'
  | 'exported'
  | 'sent'
  | 'sevis_submitted'
  | 'ds2019_issued'
  | 'closed';

export interface SponsorProfile {
  sponsorId: string;
  name: string;
  tier: 'rti' | 'batch';
  active: boolean;
  contactEmail?: string;
  sevisProgramNumber?: string;
  sevisUserId?: string;
  supportedCategories: string[];
  defaultExportFormat?: 'packet' | 'batch';
  assignedUserIds?: string[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SponsorshipReadinessResult {
  ready: boolean;
  missingFields: string[];
  missingDocs: string[];
  warnings: string[];
}

export interface SponsorAuditLog {
  logId: string;
  applicationId: string;
  sponsorId: string;
  actorUid: string;
  action:
    | 'sponsor_assignment'
    | 'sponsor_export'
    | 'sevis_record_entry'
    | 'ds2019_record_entry'
    | 'sponsor_request_changes';
  timestamp: string;
  metadata?: Record<string, any>;
}

/* ── T/IPP (Training/Internship Placement Plan) Types ── */

/** Participant info required when adding a full TIPP */
export interface TippParticipantInfo {
  emailAddress: string;
  fieldOfStudy: string;
  yearsOfExperience?: number;
  typeOfDegree: string;        // e.g. Bachelor, Master, PhD, Diploma
  dateAwardedOrExpected: string; // yyyy-MM-dd
}

export interface TrainingPhase {
  phaseName: string;
  startDate: string;      // yyyy-MM-dd
  endDate: string;        // yyyy-MM-dd
  description?: string;   // legacy free-text (kept for backward compat)

  // SEVIS TippPhase fields
  trainingField: string;
  superLastName: string;
  superFirstName: string;
  superMiddleInitial?: string;
  superTitle: string;
  superEmail: string;
  superPhone: string;       // 10 digits
  superPhoneExt?: string;
  superSignatureDate?: string; // yyyy-MM-dd
  evRole: string;            // 1-3000 chars
  goalsAndObjectives: string;
  supervisorAndQualifications: string;
  culturalActivities: string;
  skillsLearned: string;
  teachingMethod: string;
  howCompetencyMeasured: string;
  additionalRemarks?: string;
}

export interface TrainingSite {
  siteId?: string;     // required only when siteName duplicates another; SEVIS SiteIdType
  siteName: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;       // valid US state/territory abbreviation
  postalCode: string;  // 5-digit (SEVIS USPostalType)

  // SEVIS extended site fields
  employerID: string;
  fullTimeEmployees: number;
  annualRevenue: string;     // e.g. "0-500000", "500001-1000000", etc (AnnualRevenue)
  websiteURL: string;
  workersCompInd: 'Y' | 'N';
  workersCompCarrier?: string;
  workersCompForEvInd: 'Y' | 'N' | 'E';
  evHoursPerWeek: number;    // 1-99
  stipendInd: 'Y' | 'N';
  stipendAmount?: number;
  stipendFrequency?: string;  // Hourly, Weekly, Biweekly, Monthly, Annually
  nonMonetaryComp?: string;

  // Site-level supervisor (TippSite supervisor fields)
  supervisorLastName: string;
  supervisorFirstName: string;
  supervisorTitle: string;
  supervisorPhone: string;
  supervisorPhoneExt?: string;
  supervisorEmail: string;
  supervisorFax?: string;

  // Signature dates
  officialUserName?: string;
  officialSignatureDate?: string; // yyyy-MM-dd
  evSignatureDate?: string;       // yyyy-MM-dd

  phases: TrainingPhase[];
}

export interface TrainingPlan {
  sites: TrainingSite[];
  participantInfo?: TippParticipantInfo;
}

export interface CoverageGap {
  from: string;  // yyyy-MM-dd
  to: string;    // yyyy-MM-dd
}

export interface CoverageResult {
  valid: boolean;
  gaps: CoverageGap[];
  overlaps: { start: string; end: string }[];
  totalCoverageDays: number;
  programDays: number;
}

/** SEVIS batch-level types */
export interface SevisBatchAddress {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postalCode: string; // 5-digit
}

export interface SevisFinancialInfo {
  receivedUSGovtFunds: 'Y' | 'N';
  programSponsorFunds?: number;
  otherFunds?: {
    personal?: number;
    evGovt?: number;
    other?: { name: string; amount: number };
  };
}

/** Foreign address for SEVIS export */
export interface SevisForeignAddress {
  address1: string;
  address2?: string;
  city: string;
  province?: string;
  countryCode: string;
  postalCode?: string;
}

/** Dependent for SEVIS CreateDependent block */
export interface SevisDependent {
  lastName: string;
  firstName: string;
  middleName?: string;
  passportName?: string;
  preferredName?: string;
  suffix?: string;
  birthDate: string;
  sex: 'M' | 'F';
  dependentType: 'SPOUSE' | 'CHILD';
  countryCode?: string;
  phoneNumber?: string;
}

/** SEVIS EV Create Reason codes */
export type EvCreateReason =
  | '01' // Initial entry
  | '02' // Change of status
  | '03' // Reinstatement
  | '04' // Transfer
  | '05' // Change of category
  | '06'; // Other

/** Daily.co Workspace Meeting Configuration */
export interface DailyWorkspaceSettings {
  jitsiEnabled?: boolean;
  dailyEnabled: boolean;
  apiKey?: string; // Masked ("••••••••••••••••••••") on retrieval
  domain?: string; // Optional custom Daily domain or subdomain
  defaultPrivacy: 'private' | 'public';
  startVideoOff: boolean;
  startAudioOff: boolean;
  enableChat: boolean;
  enableScreenShare: boolean;
  enableRecording: 'cloud' | 'local' | 'off';
  defaultVideoProvider?: 'jitsi' | 'daily';
  updatedAt?: string;
}

export interface WorkspaceConfig {
  workspaceId: string;
  orgName?: string;
  branding?: any;
  defaultVideoProvider?: 'jitsi' | 'daily';
  dailySettings?: DailyWorkspaceSettings;
  llmConfig?: any;
}

/* ── Finance & Invoicing Subsystem Types ── */

export interface FinanceProduct {
  id: string;
  workspaceId: string;
  name: string;
  productId?: string; // Optional user-defined product code e.g. CT-001
  description: string; // Product description (up to 500 words)
  sellingPrice: number; // Customer price
  price?: number; // Alias for sellingPrice
  costPrice: number; // Internal cost (STRICTLY CONFIDENTIAL - never exposed to applicants)
  taxRate?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface InvoiceLineItem {
  id: string;
  productId?: string; // Reference to catalogue product if selected
  name: string; // Snapshot product name or custom item name
  description: string; // Snapshot description or custom item description
  quantity: number;
  rate: number; // Unit selling price snapshot
  discountType?: 'fixed' | 'percentage';
  discountValue?: number; // e.g. 50 ($50) or 10 (10%)
  discountAmount: number; // Computed discount dollar amount
  taxRate?: number;
  taxAmount?: number;
  amount: number; // Line total: (quantity * rate) - discountAmount + taxAmount
  isCustom?: boolean; // True if custom line item not from catalogue
}

export type InvoiceStatus = 'Draft' | 'Sent' | 'Partially Paid' | 'Paid' | 'Written Off' | 'Cancelled' | 'Void' | 'Overdue';

export type QuoteStatus = 'Draft' | 'Sent' | 'Viewed' | 'Accepted' | 'Declined' | 'Expired' | 'Converted';

export interface FinanceAttachment {
  id: string;
  fileName: string;
  fileUrl: string;
  storagePath?: string;
  fileSize?: number;
  mimeType?: string;
  uploadedAt: string;
  uploadedBy?: string;
}

export interface QuoteRecord {
  id?: string;
  quoteId: string;
  workspaceId: string;
  quoteNumber: string; // Auto-generated e.g. QTE-2026-000001
  issueDate: string; // ISO yyyy-MM-dd
  expirationDate: string; // ISO yyyy-MM-dd
  status: QuoteStatus;
  applicantId: string; // Application ID
  applicantUserId: string; // User UID
  applicantName: string;
  applicantEmail: string;
  applicantAddress?: string;
  programId?: string;
  programName?: string;
  term?: string;
  referrerId?: string;
  referrerName?: string;
  sponsorId?: string;
  sponsorName?: string;
  workspaceName?: string;
  workspaceLogoUrl?: string;
  workspaceAddress?: string;
  workspaceEmail?: string;
  workspacePhone?: string;
  items: InvoiceLineItem[];
  subtotal: number;
  totalDiscount: number;
  discountType?: 'percent' | 'fixed';
  discountValue?: number;
  taxTotal: number;
  taxPercent?: number;
  adjustment: number;
  adjustmentReason?: string;
  total: number;
  currency: string;
  description?: string;
  notes?: string;
  internalNotes?: string;
  terms?: string;
  attachments?: FinanceAttachment[];
  sentAt?: string;
  sentBy?: string;
  viewedAt?: string;
  acceptedAt?: string;
  declinedAt?: string;
  declineReason?: string;
  convertedAt?: string;
  convertedBy?: string;
  convertedInvoiceId?: string;
  convertedInvoiceNumber?: string;
  pdfStoragePath?: string;
  pdfUrl?: string;
  createdAt: string;
  createdBy: string;
  createdByName?: string;
  updatedAt: string;
  updatedBy?: string;
}

export interface InvoiceRecord {
  id?: string;
  amount?: number; // Total amount paid or balance
  invoiceId: string;
  workspaceId: string;
  invoiceNumber: string; // Auto-generated sequential e.g. INV-2026-000001
  invoiceDate: string; // ISO yyyy-MM-dd
  dueDate: string; // ISO yyyy-MM-dd
  status: InvoiceStatus;
  applicantId: string; // Application ID
  applicantUserId: string; // User UID
  applicantName: string;
  applicantEmail: string;
  applicantAddress?: string;
  programId?: string;
  programName?: string;
  term?: string;
  referrerId?: string;
  referrerName?: string;
  sponsorId?: string;
  sponsorName?: string;
  workspaceName?: string;
  workspaceLogoUrl?: string;
  workspaceAddress?: string;
  workspaceEmail?: string;
  workspacePhone?: string;
  items: InvoiceLineItem[];
  subtotal: number;
  totalDiscount: number;
  taxTotal: number;
  adjustment: number; // Manual dollar adjustment (+ or -)
  adjustmentReason?: string;
  total: number;
  amountPaid: number;
  amountDue: number;
  currency: string; // e.g. USD
  description?: string; // Short summary
  notes?: string; // Customer-facing notes
  internalNotes?: string; // Admin-only internal notes
  terms?: string; // Payment terms & instructions
  attachments?: FinanceAttachment[];
  quoteId?: string;
  quoteNumber?: string;
  sentAt?: string;
  sentBy?: string;
  paidAt?: string;
  paidBy?: string;
  paymentMethod?: string;
  paymentReference?: string;
  writtenOffAt?: string;
  writtenOffBy?: string;
  writeOffReason?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancelReason?: string;
  pdfStoragePath?: string;
  pdfUrl?: string;
  fileName?: string;
  storagePath?: string;
  uploadedBy?: string;
  uploadedAt?: string;
  createdAt: string;
  createdBy: string;
  createdByName?: string;
  updatedAt: string;
  updatedBy?: string;
}

export interface FinanceReminderRule {
  id: string;
  enabled: boolean;
  days: number;
  direction: 'before' | 'after' | 'on_due_date';
  subject: string;
  message: string;
  sendToApplicant: boolean;
  ccEmails?: string[];
  onlyIfUnpaid: boolean;
}

export interface FinanceReminderSettings {
  rules: FinanceReminderRule[];
  updatedAt?: string;
  updatedBy?: string;
}

export interface FinancialAuditLogRecord {
  recipient?: string;
  paymentLink?: string;
  id?: string;
  workspaceId: string;
  timestamp: string;
  actor: string;
  action: string;
  entityType: 'Quote' | 'Invoice' | 'Payment' | 'Allocation' | 'Reminder' | 'StripeWebhook' | 'Credit';
  entityId: string;
  amount?: number;
  currency?: string;
  details?: string;
  metadata?: Record<string, any>;
}

export interface FinanceDashboardStats {
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  totalWrittenOff: number;
  draftCount: number;
  sentCount: number;
  paidCount: number;
  writtenOffCount: number;
  invoiceCount: number;
}

export interface WorkspaceStripeSettings {
  publishableKey: string;
  secretKey?: string; // Masked ("••••••••••••••••••••") on retrieval
  status: 'Connected' | 'Not Connected';
  lastVerifiedAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface PaymentLinkRecord {
  id: string;
  token: string;
  invoiceId: string;
  invoiceNumber: string;
  applicantId: string;
  applicantUserId: string;
  workspaceId: string;
  active: boolean;
  createdBy: string;
  createdAt: string;
  revokedAt?: string;
}

export interface PaymentRecord {
  id: string;
  paymentId?: string;
  workspaceId: string;
  applicantId: string; // Application ID
  applicantUserId: string; // User UID
  invoiceId: string;
  invoiceNumber?: string;
  paymentToken?: string;
  stripePaymentIntentId?: string;
  stripeCheckoutSessionId?: string;
  amount: number;
  currency: string;
  paymentDate: string;
  paymentMethod: 'Stripe' | string;
  status: 'Pending' | 'Approved' | 'Failed' | 'Refunded' | 'Cancelled';
  transactionReference?: string;
  receiptUrl?: string;
  createdAt: string;
  updatedAt?: string;
}

/* ── Personal SMTP/IMAP Mailbox Connection Types ── */

export type MailboxProvider = 'gmail' | 'outlook' | 'zoho' | 'custom';
export type EncryptionMethod = 'SSL/TLS' | 'STARTTLS' | 'None';

export interface MailboxConnection {
  id: string;
  workspaceId: string;
  userId: string;
  userEmail: string;
  email: string;
  displayName?: string;
  provider: MailboxProvider;
  imapHost: string;
  imapPort: number;
  imapSecurity: EncryptionMethod;
  smtpHost: string;
  smtpPort: number;
  smtpSecurity: EncryptionMethod;
  username: string;
  // Encrypted credential block
  encryptedPassword?: {
    iv: string;
    ciphertext: string;
    authTag: string;
  };
  status: 'active' | 'reconnect_required' | 'error';
  lastTestedAt?: string;
  lastError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SyncedEmailMessage {
  id: string;
  mailboxId: string;
  mailboxEmail: string;
  workspaceId: string;
  applicantEmail: string;
  messageId: string;
  uid?: number;
  subject: string;
  from: string;
  fromName?: string;
  to: string;
  toName?: string;
  cc?: string;
  bcc?: string;
  date: string;
  textBody?: string;
  htmlBody?: string;
  folder?: string;
  direction: 'inbound' | 'outbound';
  hasAttachments?: boolean;
  attachments?: { name: string; mimeType: string; size?: number }[];
  syncedAt: string;
}

export interface SyncedEmailThread {
  applicantEmail: string;
  mailboxId: string;
  lastSyncedAt: string;
  messageCount: number;
  messages: SyncedEmailMessage[];
}

/* ── Staged Document AI Pipeline & Job Matching Types ── */

export type AiSuggestionType =
  | 'document_review'
  | 'program_eligibility'
  | 'job_matching'
  | 'partner_suggestions'
  | 'red_flag';

export interface AiSuggestionRecord {
  id?: string;
  workspaceId?: string;
  type: AiSuggestionType;
  sourceDocIds?: string[];
  modelTier: 'cheap' | 'escalated';
  modelUsed: string;
  confidence: number;
  payload: Record<string, any>;
  explanation: string;
  reasoning?: string;
  suggestedData?: Record<string, any>;
  status: 'pending' | 'pending_review' | 'accepted' | 'rejected' | 'overridden' | 'ignored';
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt: string;
  targetRecordId?: string;
  targetRecordCollection?: string;
  applicantId?: string;
  applicationId?: string;
}

export interface AiPipelineConfig {
  minFieldConfidence: number;      // below this, field excluded from judgment + flagged needs_manual_review (default 0.75)
  minJudgmentConfidence: number;   // below this, auto-escalate to stronger model (default 0.6)
  matchShortlistSize: number;      // top-N applicants per job that get an LLM explanation (default 5)
  forceRescanDefault: boolean;     // default false
}

export const DEFAULT_AI_PIPELINE_CONFIG: AiPipelineConfig = {
  minFieldConfidence: 0.75,
  minJudgmentConfidence: 0.6,
  matchShortlistSize: 5,
  forceRescanDefault: false,
};
